import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import DOMPurify from "dompurify";
import { NodeProps, Node, useReactFlow, useNodes, useEdges } from "@xyflow/react";
import { Send, Plus, Loader2, ChevronDown, Sparkles, X, AlertTriangle, Mail, FileText } from "lucide-react";
import outlookService from "../../services/outlook.service";
import syntaxHighlight from "../../utils/jsonViewer";
import WorkflowBaseNode from "../../common/shared/WorkflowBaseNode";
import WorkflowNodeModal from "../../common/shared/WorkflowNodeModal";
import { RichTextEditor } from "../../../components/RichTextEditor";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";

type CredentialItem = {
  _id: string;
  user_info: {
    user_email: string;
    user_name: string;
  };
};

export const OutlookSendNode: React.FC<NodeProps<Node<any>>> = ({ id, data, selected }) => {
  const { setNodes, getNodes } = useReactFlow();
  const allFlowNodes = useNodes();
  const allFlowEdges = useEdges();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [hasLoadedCredentials, setHasLoadedCredentials] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>(
    data?.saved_html || data?.inputs?.saved_html || "<p></p>"
  );

  // Ancestor nodes (needed for variables sidebar mapping)
  const previousNodes = useMemo(() => {
    const ancestors = getAllAncestorNodes(id, allFlowNodes, allFlowEdges);
    return ancestors.map((n) => ({
      nodeId: n.id,
      nodeName: n.data?.label || n.data?.name || n.id,
      nodeType: n.type || "unknown",
      inputs: n.data?.inputs || {},
      outputs: n.data?.outputs || {},
    })) as any[];
  }, [id, allFlowNodes, allFlowEdges]);

  // Flatten variables for RichTextEditor preview
  const previewVariables = useMemo(() => {
    const vars: Record<string, any> = {};
    previousNodes.forEach((node) => {
      Object.entries(node.outputs).forEach(([key, value]) => {
        vars[`${node.nodeName}.outputs.${key}`] = value;
      });
      Object.entries(node.inputs).forEach(([key, value]) => {
        vars[`${node.nodeName}.inputs.${key}`] = value;
      });
    });
    return vars;
  }, [previousNodes]);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState<string | null>(null);

  const authPopupRef = useRef<Window | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedCredentialId, setSelectedCredentialId] = useState<string>(data?.outlook_credential_id || data?.inputs?.outlook_credential_id || "");

  const [to, setTo] = useState<string>(() => {
    const initialTo = data?.to || data?.inputs?.to;
    if (Array.isArray(initialTo)) return initialTo.join(", ");
    return initialTo || "";
  });

  const [subject, setSubject] = useState<string>(data?.subject || data?.inputs?.subject || "");

  const [textEditorPrompt, setTextEditorPrompt] = useState<string>(data?.ai_prompt || data?.inputs?.ai_prompt || "");
  // Attachments: single URL, multiple URLs (one per line / comma-separated), OR a workflow variable
  const [attachments, setAttachments] = useState<string>(() => {
    const raw = data?.attachments ?? data?.inputs?.attachments;
    if (Array.isArray(raw)) return raw.join("\n");
    if (typeof raw === "string") return raw;
    return "";
  });

  const [credentials, setCredentials] = useState<CredentialItem[]>([]);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const allowed = ["http://localhost:3000", "http://localhost:5173", "https://nightly-dms.skill-mine.com", "https://demo-dms.skill-mine.com"];
      if (!allowed.includes(event.origin)) return;

      if (
        event.data?.type === "OUTLOOK_AUTH_SUCCESS" ||
        event.data?.status === "success" ||
        (typeof event.data === 'string' && event.data.includes('success'))
      ) {
        setConnecting(false);
        loadCredentials();
        if (authPopupRef.current && !authPopupRef.current.closed) authPopupRef.current.close();
      }
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  useEffect(() => {
    if (!connecting) return;
    const pollInterval = setInterval(() => {
      const popup = authPopupRef.current;
      if (popup && popup.closed) {
        authPopupRef.current = null;
        setConnecting(false);
        loadCredentials();
        clearInterval(pollInterval);
      }
    }, 1000);
    return () => clearInterval(pollInterval);
  }, [connecting]);

  const openAuthPopup = async () => {
    try {
      setConnecting(true);
      setErrorMsg(null);
      const templateId = data?.template_id || localStorage.getItem("template_id");
      const authUrl = outlookService.getOutlookLoginUrl(templateId || undefined);
      const width = 500, height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const popup = window.open(authUrl, "OutlookAuthPopup", `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
      if (!popup) throw new Error("Popup blocked! Please allow popups for this site.");
      authPopupRef.current = popup;
      popup.focus();
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to open login popup");
      setConnecting(false);
    }
  };

  const loadCredentials = async () => {
    if (loadingCredentials) return;
    const templateId = data?.template_id || localStorage.getItem("template_id");
    try {
      setLoadingCredentials(true);
      const response = await outlookService.listCredentials(templateId);
      if (response && response.success) {
        setCredentials(response.data || []);
      }
      setHasLoadedCredentials(true);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load credentials");
    } finally {
      setLoadingCredentials(false);
    }
  };

  useEffect(() => {
    if (isModalOpen && !hasLoadedCredentials && !loadingCredentials) {
      loadCredentials();
    }
  }, [isModalOpen]);

  const selectedCredentialInfo = useMemo(() => {
    const cred = credentials.find((c) => c._id === selectedCredentialId);
    return cred?.user_info || data?.selected_credential_info || null;
  }, [credentials, selectedCredentialId, data?.selected_credential_info]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== id) return n;
        const toArray = to.split(",").map(s => s.trim()).filter(Boolean);
        const nodeData = n.data as Record<string, any>;
        const cleanedData = { ...nodeData };
        delete cleanedData.inputs;
        delete cleanedData.selected_credential_info;

        // Normalise attachments: single workflow expression stays as a string;
        // otherwise split into URL list (one per line / comma).
        const trimmedAtt = attachments.trim();
        let attachmentsVal: any = "";
        if (trimmedAtt) {
          const looksLikeSingleExpression = /^\{\{[^}]+\}\}$/.test(trimmedAtt);
          if (looksLikeSingleExpression) {
            attachmentsVal = trimmedAtt;
          } else {
            const parts = trimmedAtt.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
            attachmentsVal = parts.length <= 1 ? (parts[0] ?? "") : parts;
          }
        }

        return {
          ...n,
          data: {
            ...cleanedData,
            outlook_credential_id: selectedCredentialId,
            to: toArray,
            subject,
            ai_prompt: textEditorPrompt,
            saved_html: previewHtml,
            attachments: attachmentsVal,
          },
        };
      })
    );
  }, [selectedCredentialId, to, subject, textEditorPrompt, previewHtml, attachments, id, setNodes]);

  const generateTextEditorPreview = async () => {
    try {
      setGeneratingPreview(true);
      setErrorMsg(null);

      const allNodes = getNodes();
      const workflowContext: Record<string, any> = {};
      allNodes.forEach(node => {
        const outputs = (node.data as any)?.outputs || (node.data as any)?.results;
        if (outputs && typeof outputs === 'object' && !Array.isArray(outputs)) {
          Object.assign(workflowContext, outputs);
        }
      });

      const response = await outlookService.generateTemplate(textEditorPrompt || "", [JSON.stringify(workflowContext)]);

      if (response && response.data && response.data.html) {
        setPreviewHtml(
          DOMPurify.sanitize(response.data.html, {
            ADD_TAGS: ["style"],
            ADD_ATTR: ["target", "rel"],
          })
        );
      } else {
        setErrorMsg("Preview content could not be generated.");
      }
    } catch (err: any) {
      setErrorMsg("Failed to generate: " + (err.response?.data?.message || err.message));
    } finally {
      setGeneratingPreview(false);
    }
  };

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!selectedCredentialId) errors.push("Outlook account is required");
    if (!to.trim()) errors.push("Recipient email (To) is required");
    if (!subject.trim()) errors.push("Subject is required");
    const hasHtmlContent = previewHtml && previewHtml.trim() !== "" && previewHtml.trim() !== "<p></p>";
    if (!textEditorPrompt.trim() && !hasHtmlContent) {
      errors.push("Email content or prompt is required");
    }
    return errors;
  }, [selectedCredentialId, to, subject, textEditorPrompt, previewHtml]);

  const isConfigured = validationErrors.length === 0;

  const renderOutputsSidebar = useCallback(() => (
    <div className="w-80 bg-slate-50 overflow-y-auto p-4 border-l [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full" />
          Node Outputs
        </h3>
        <p className="text-xs text-slate-500">Execution result</p>
      </div>
      {data.outputs ? (
        <div className="bg-white border rounded-lg p-3">
          <pre className="text-xs font-mono break-all whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: syntaxHighlight(data.outputs) }} />
        </div>
      ) : (
        <div className="p-6 bg-white border border-dashed rounded-lg text-center">
          <Send className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-xs text-slate-500 font-medium">No output yet</p>
        </div>
      )}
    </div>
  ), [data.outputs]);

  const handleDrop = (e: React.DragEvent, targetField: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(null);
    const variable = e.dataTransfer.getData("text/plain");
    if (!variable) return;
    if (targetField === "to") setTo(prev => prev + (prev ? ", " : "") + variable);
    else if (targetField === "subject") setSubject(prev => prev + (prev ? " " : "") + variable);
    else if (targetField === "ai_prompt") {
      setTextEditorPrompt(prev => prev + (prev ? " " : "") + variable);
      setPreviewHtml(null);
      setIsPreviewModalOpen(false);
    }
    else if (targetField === "attachments") {
      setAttachments(prev => prev ? `${prev}\n${variable}` : variable);
    }
  };

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="Outlook Send"
      icon={<Send className="w-4 h-4 text-blue-600" />}
      nodeDocsType="OUTLOOK_SEND"
      themeColor="blue"
      configureLabel="Configure Email"
      isConfigured={isConfigured}
      validationErrors={validationErrors}
      onConfigure={() => setIsModalOpen(true)}
      hasSourceHandle={true}
      hasErrorHandle={false}
    >
      {selectedCredentialInfo && (
        <div className="mt-2 pt-2 border-t text-[10px] text-slate-500">
          <p className="truncate">From: <span className="font-medium text-slate-700">{selectedCredentialInfo.user_email}</span></p>
          {to && <p className="truncate">To: <span className="font-medium text-slate-700">{to}</span></p>}
        </div>
      )}

      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Outlook Configuration"
        icon={<Mail className="w-5 h-5 text-blue-600" />}
        themeColor="blue"
        nodeId={id}
        nodeData={data}
        nodeIcon={<Send className="w-6 h-6 text-slate-400" />}
        validationStatus={{ isValid: isConfigured, errors: validationErrors }}
        renderOutputsSidebar={renderOutputsSidebar}
      >
        <div className="py-2">
          <div className="space-y-5">
            {/* Top Row: Connection Settings & Email Details side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
              {/* Connection Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 rounded text-blue-600">
                      <Mail className="w-4 h-4" />
                    </div>
                    Connection Settings
                  </h3>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Sender Account <span className="text-red-500">*</span></label>
                      <div className="relative" ref={dropdownRef}>
                        <div onClick={() => { if (!isDropdownOpen) { loadCredentials(); } setIsDropdownOpen(!isDropdownOpen); }}
                          className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer flex items-center justify-between hover:border-blue-400 transition-colors">
                          <span className={selectedCredentialId ? "text-gray-900 font-medium" : "text-gray-400"}>
                            {selectedCredentialInfo ? `${selectedCredentialInfo.user_name} (${selectedCredentialInfo.user_email})` : "Select Outlook Account..."}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>
                        {isDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto py-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
                            {credentials.map((cred) => (
                              <div key={cred._id} onClick={() => { setSelectedCredentialId(cred._id); setIsDropdownOpen(false); }}
                                className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 flex flex-col ${selectedCredentialId === cred._id ? "bg-blue-50 border-l-2 border-blue-500" : "text-gray-700"}`}>
                                <span className="font-medium text-gray-900">{cred.user_info.user_name}</span>
                                <span className="text-xs text-gray-500">{cred.user_info.user_email}</span>
                              </div>
                            ))}
                            <button onClick={(e) => { e.stopPropagation(); openAuthPopup(); setIsDropdownOpen(false); }}
                              className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 border-t border-gray-100 mt-1 font-medium">
                              <Plus className="w-4 h-4" /> Add New Account
                            </button>
                          </div>
                        )}
                      </div>
                      {errorMsg && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {errorMsg}</p>}
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-[10px] text-gray-400 leading-normal">
                  Connect and select the Microsoft Outlook account from which this node will send automated outgoing emails.
                </div>
              </div>

              {/* Email Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded text-blue-600">
                    <Send className="w-4 h-4" />
                  </div>
                  Email Details
                </h3>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Recipient (To) <span className="text-red-500">*</span></label>
                    <input type="text" value={to} onChange={(e) => setTo(e.target.value)} onDrop={(e) => handleDrop(e, "to")} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
                      className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="e.g. user@example.com" />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Subject Line <span className="text-red-500">*</span></label>
                    <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} onDrop={(e) => handleDrop(e, "subject")} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
                      className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="Enter email subject" />
                  </div>

                  {/* Attachments (optional) — accepts URL(s) or a workflow variable */}
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                      <span>
                        Attachments <span className="text-gray-400 font-normal">(optional)</span>
                      </span>
                      {attachments && (
                        <button
                          type="button"
                          onClick={() => setAttachments("")}
                          className="text-[10px] text-gray-400 hover:text-red-500"
                        >
                          Clear
                        </button>
                      )}
                    </label>
                    <textarea
                      value={attachments}
                      onChange={(e) => setAttachments(e.target.value)}
                      onDrop={(e) => handleDrop(e, "attachments")}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; setIsDraggingOver("attachments"); }}
                      onDragLeave={() => setIsDraggingOver(null)}
                      rows={2}
                      placeholder={'Paste a file URL, list multiple URLs (one per line), OR drag a variable like {{DocumentStoreNode.outputs.file_ids}}'}
                      className={`w-full p-2.5 text-xs font-mono border-2 rounded-lg outline-none transition-colors resize-none ${isDraggingOver === "attachments"
                          ? "border-blue-300 bg-blue-50"
                          : attachments
                            ? "border-green-300 bg-green-50 text-green-800"
                            : "border-dashed border-gray-300 bg-gray-50 focus:border-blue-500 focus:bg-white"
                        }`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Row: Email Body Editor (full width) */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col min-h-[480px] h-full">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <div className="p-1.5 bg-indigo-100 rounded text-indigo-600">
                  <FileText className="w-4 h-4" />
                </div>
                Email Body Editor
              </h3>

              {/* AI Helper banner */}
              <div className="bg-slate-50 border border-gray-200 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-500 animate-pulse" />
                    AI Template Generator
                  </label>
                  <button 
                    type="button"
                    onClick={generateTextEditorPreview} 
                    disabled={generatingPreview || !textEditorPrompt.trim()}
                    className="flex items-center gap-1.5 px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-medium rounded transition-colors disabled:opacity-50"
                  >
                    {generatingPreview ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Generate
                  </button>
                </div>
                <input
                  type="text"
                  value={textEditorPrompt}
                  onChange={(e) => {
                    setTextEditorPrompt(e.target.value);
                  }}
                  onDrop={(e) => handleDrop(e, "ai_prompt")}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
                  placeholder="e.g., 'Write a professional outage notification'..."
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded outline-none focus:border-indigo-500 bg-white"
                />
              </div>

              {/* Rich Text Editor Component */}
              <div className="flex-1 flex flex-col min-h-[320px] bg-white border border-gray-200 rounded-lg overflow-hidden">
                <RichTextEditor
                  content={previewHtml}
                  onChange={(html) => {
                    setPreviewHtml(html);
                  }}
                  placeholder="Type your email content here, or generate it using the AI Template Generator above..."
                  variables={previewVariables}
                />
              </div>
            </div>
          </div>
        </div>
      </WorkflowNodeModal>

      {/* Simple Email Preview Modal */}
      {isPreviewModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10001] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-blue-50 rounded-lg text-blue-600">
                  <Mail className="w-5 h-5" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">Email Preview</h2>
              </div>
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 bg-slate-50 p-4 md:p-8 overflow-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-300">
              <div className="bg-white w-full max-w-4xl mx-auto shadow-sm border border-slate-200 rounded-lg p-8 min-h-full">
                {previewHtml ? (
                  <div className="email-preview-content" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400 py-20">
                    <Mail className="w-12 h-12 mb-2 opacity-20" />
                    <p className="text-sm">No preview content generated.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer with Close Button */}
            <div className="px-6 py-4 border-t bg-white flex justify-end">
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="px-8 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </WorkflowBaseNode>
  );
};

export default OutlookSendNode;